"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { shouldSkipTracking } from "@/lib/consent";
import { parsePathname } from "@/lib/i18n/config";
import {
  ANALYTICS_ENDPOINT,
  type BeaconPageview,
  type BeaconPayload,
  type ContactArea,
  type HoldClickProps,
} from "@/lib/analytics/events";

/**
 * Intern, cookiefri måling (kontrakt: lib/analytics/events.ts). Renderer intet.
 *
 * - Sidevisning ved hver ny pathname (også soft navigation) og ved bfcache-gendannelse.
 * - Aktiv tid: tæller kun mens fanen er synlig OG brugeren har været aktiv inden
 *   for 2 min. Beregnes som deltaer fra performance.now() ved tilstandsskift —
 *   intet arbejde pr. frame og ingen timere. Sendes ved skjul, pagehide og
 *   inden næste sidevisning.
 * - Klik: én delegeret lytter på document for "Tilmeld Hold" (GonDrive) samt
 *   tel:/sms:/mailto:. Kalder aldrig preventDefault/stopPropagation, så GTM's
 *   egne link-triggere virker uændret.
 *
 * Ingen cookies, intet i localStorage/sessionStorage. Alt er pakket i try/catch:
 * måling må aldrig kaste eller påvirke siden.
 */

/** Aktivitet ældre end dette tæller ikke som aktiv tid. */
const ACTIVE_WINDOW_MS = 120_000;
/** Aktivitets-events (især mousemove) behandles højst én gang pr. sekund. */
const ACTIVITY_THROTTLE_MS = 1_000;
const MIN_ENGAGEMENT_MS = 500;
/** Collectorens loft pr. ping. */
const MAX_ENGAGEMENT_MS = 1_800_000;
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart", "mousemove"] as const;
const CONTACT_AREAS: readonly string[] = ["header", "footer", "sticky", "menu", "side"] satisfies ContactArea[];
const CONTACT_EVENTS = { tel: "phone_click", sms: "sms_click", mailto: "email_click" } as const;

type Engagement = {
  /** Aktiv sidevisnings id; null når der ikke måles. */
  id: string | null;
  /** Optalt, endnu ikke sendt aktiv tid. */
  ms: number;
  /** performance.now() for seneste optælling. */
  since: number;
  /** performance.now() for seneste brugeraktivitet. */
  lastActive: number;
  visible: boolean;
};

/** Pakker en handler ind, så en fejl i målingen aldrig når ud til siden. */
function safe<A extends unknown[]>(fn: (...args: A) => void): (...args: A) => void {
  return (...args) => {
    try {
      fn(...args);
    } catch {
      // Måling må aldrig påvirke siden.
    }
  };
}

function isDisabled(pathname: string): boolean {
  return shouldSkipTracking(pathname) || navigator.webdriver === true;
}

function send(payload: BeaconPayload): void {
  try {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon?.(ANALYTICS_ENDPOINT, new Blob([body], { type: "text/plain" }))) return;
    fetch(ANALYTICS_ENDPOINT, {
      method: "POST",
      keepalive: true,
      body,
      headers: { "content-type": "text/plain" },
    }).catch(() => {});
  } catch {
    // Måling må aldrig påvirke siden.
  }
}

function uuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback (fx usikker kontekst): RFC 4122 v4 fra getRandomValues/Math.random.
  const b = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(b);
  } else {
    for (let i = 0; i < b.length; i++) b[i] = Math.floor(Math.random() * 256);
  }
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function pushDataLayer(data: Record<string, unknown>): void {
  const w = window as Window & { dataLayer?: unknown[] };
  (w.dataLayer = w.dataLayer || []).push(data);
}

/** Lægger aktiv, synlig tid siden sidste optælling til (højst op til 2 min efter sidste aktivitet). */
function count(e: Engagement, now = performance.now()): void {
  if (e.id && e.visible) {
    const end = Math.min(now, e.lastActive + ACTIVE_WINDOW_MS);
    if (end > e.since) e.ms += end - e.since;
  }
  e.since = now;
}

/** Sender optalt aktiv tid for den aktuelle sidevisning (hvis ≥ 500 ms). */
function flush(e: Engagement): void {
  count(e);
  if (!e.id || e.ms < MIN_ENGAGEMENT_MS) return;
  send({ t: "en", id: e.id, ms: Math.min(Math.round(e.ms), MAX_ENGAGEMENT_MS) });
  e.ms = 0;
}

/** Starter en ny sidevisning: nulstiller optællingen og sender pageview-beacon'en. */
function startPageview(e: Engagement, pathname: string, referrer: string | null): void {
  const id = uuid();
  const now = performance.now();
  e.id = id;
  e.ms = 0;
  e.since = now;
  e.lastActive = now;
  e.visible = document.visibilityState === "visible";

  const q = new URLSearchParams(location.search);
  const param = (k: string) => q.get(k)?.trim().slice(0, 100) || undefined;
  const pv: BeaconPageview = {
    t: "pv",
    id,
    p: pathname.slice(0, 300),
    r: referrer ? referrer.slice(0, 1000) : null,
  };
  const s = param("utm_source");
  const m = param("utm_medium");
  const c = param("utm_campaign");
  if (s || m || c) pv.u = { s, m, c };
  if (q.has("gclid") || q.has("gbraid") || q.has("wbraid")) pv.g = 1;
  if (q.has("fbclid")) pv.f = 1;
  send(pv);
}

/** Kører fn nu — eller først når en prerendret side (Chrome speculation rules) faktisk vises. */
function whenActivated(fn: () => void): void {
  if ((document as Document & { prerendering?: boolean }).prerendering) {
    document.addEventListener("prerenderingchange", fn, { once: true });
  } else {
    fn();
  }
}

function readHoldProps(el: Element): HoldClickProps | null {
  try {
    const v: unknown = JSON.parse(el.getAttribute("data-track-props") ?? "");
    return v && typeof v === "object" ? (v as HoldClickProps) : null;
  } catch {
    return null;
  }
}

/** Props for GonDrive-links uden data-track (fx links indsat via CMS'et). */
function fallbackHoldProps(url: URL | null, pathname: string): HoldClickProps {
  return {
    category: "Ukendt",
    hold_name: "Ukendt hold",
    hold_type: null,
    api_title: null,
    api_text: null,
    lesson_id: url?.searchParams.get("lesson")?.slice(0, 40) || null,
    lesson_date: null,
    placement: "andet",
    locale: parsePathname(pathname).locale,
  };
}

function isGonDriveUrl(url: URL | null): boolean {
  const host = url?.hostname.toLowerCase() ?? "";
  return host === "gondrive.com" || host.endsWith(".gondrive.com");
}

/** Hvor et kontakt-link sad: eksplicit data-track-area, ellers header/footer, ellers "side". */
function contactArea(a: Element): ContactArea {
  const area = a.closest("[data-track-area]")?.getAttribute("data-track-area");
  if (area && CONTACT_AREAS.includes(area)) return area as ContactArea;
  if (a.closest("header")) return "header";
  if (a.closest("footer")) return "footer";
  return "side";
}

const onLinkClick = safe((ev: MouseEvent) => {
  if (ev.type === "auxclick" && ev.button !== 1) return;
  const pathname = location.pathname;
  if (isDisabled(pathname)) return;
  const a = ev.target instanceof Element ? ev.target.closest("a[href]") : null;
  if (!a) return;
  const href = (a.getAttribute("href") ?? "").trim();

  const scheme = /^(tel|sms|mailto):/i.exec(href)?.[1].toLowerCase() as keyof typeof CONTACT_EVENTS | undefined;
  if (scheme) {
    send({ t: "ev", n: CONTACT_EVENTS[scheme], p: pathname, d: { area: contactArea(a) } });
    return;
  }

  const tagged = a.closest('[data-track="gondrive"]');
  let url: URL | null = null;
  try {
    url = new URL(href, location.href);
  } catch {
    // Ugyldig href — kun relevant hvis linket er tagget.
  }
  if (!tagged && !isGonDriveUrl(url)) return;

  const props = (tagged && readHoldProps(tagged)) || fallbackHoldProps(url, pathname);
  send({ t: "ev", n: "gondrive_click", p: pathname, d: props });
  pushDataLayer({
    event: "holdstart_click",
    hold_category: props.category,
    hold_name: props.hold_name,
    hold_type: props.hold_type,
    hold_api_title: props.api_title,
    hold_api_text: props.api_text,
    hold_lesson_id: props.lesson_id,
    hold_lesson_date: props.lesson_date,
    click_placement: props.placement,
    page_locale: props.locale,
  });
});

export function SiteAnalytics() {
  const pathname = usePathname();
  const engagement = useRef<Engagement>({ id: null, ms: 0, since: 0, lastActive: 0, visible: false });
  // StrictMode kører effects to gange i dev — én pathname giver præcis én sidevisning.
  const lastPathname = useRef<string | null>(null);
  // document.referrer gælder kun første sidevisning i en sideindlæsning.
  const isFirstView = useRef(true);

  // Sidevisning pr. pathname: afslut den forrige (aktiv tid), start den nye.
  useEffect(() => {
    try {
      if (!pathname || lastPathname.current === pathname) return;
      lastPathname.current = pathname;
      const referrer = isFirstView.current ? document.referrer || null : null;
      isFirstView.current = false;
      const e = engagement.current;
      flush(e);
      e.id = null;
      if (isDisabled(pathname)) return;
      whenActivated(safe(() => startPageview(e, pathname, referrer)));
    } catch {
      // Måling må aldrig påvirke siden.
    }
  }, [pathname]);

  // Aktiv tid, livscyklus og klik — lyttere sættes én gang.
  useEffect(() => {
    if (navigator.webdriver === true) return;
    const e = engagement.current;

    const onActivity = safe(() => {
      const now = performance.now();
      if (now - e.lastActive < ACTIVITY_THROTTLE_MS) return;
      count(e, now);
      e.lastActive = now;
    });
    const onVisibility = safe(() => {
      if (document.visibilityState === "hidden") {
        flush(e);
        e.visible = false;
      } else {
        count(e); // flytter kun startpunktet — skjult tid tælles ikke
        e.visible = true;
        e.lastActive = performance.now();
      }
    });
    const onPageHide = safe(() => flush(e));
    // Gendannet fra bfcache (tilbage-knap) — siden er ikke genindlæst, men det er en ny visning.
    const onPageShow = safe((ev: PageTransitionEvent) => {
      if (ev.persisted && !isDisabled(location.pathname)) startPageview(e, location.pathname, null);
    });

    const passive = { passive: true, capture: true } as const;
    for (const type of ACTIVITY_EVENTS) document.addEventListener(type, onActivity, passive);
    document.addEventListener("visibilitychange", onVisibility);
    document.addEventListener("click", onLinkClick, true);
    document.addEventListener("auxclick", onLinkClick, true);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      for (const type of ACTIVITY_EVENTS) document.removeEventListener(type, onActivity, passive);
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("click", onLinkClick, true);
      document.removeEventListener("auxclick", onLinkClick, true);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  return null;
}
